import { apiSlice } from 'store/api/apiSlice';

import {
  AuditHistoryEntryResponse,
  ClinicalToolsAuditHistoryType,
  CreatePartnerCouponConfigRequest,
  FullMembershipPlanProps,
  ListAuditHistoryRequest,
  ListAuditHistoryResponse,
  ListAuditHistorySummaryEnvelope,
  ListAuditHistorySummaryResponse,
  ListMedicationClassPriorityHistoryRequest,
  ListPartnerCouponConfigsRequest,
  ListRefillSettingsHistoryRequest,
  MaxioComponentOption,
  MaxioComponentPricePointOption,
  MaxioProductPricePointOption,
  MedicationClassPriorityHistoryResponse,
  MedicationClassPriorityResponse,
  MongoObjectId,
  PartnerCouponConfigListResponse,
  PartnerCouponConfigMetaRequest,
  PartnerCouponConfigMetaResponse,
  PartnerCouponConfigResponse,
  ProgramsFullPayloadResponse,
  RefillSettingsHistoryResponse,
  RefillSettingsResponse,
  RequestMedicationTemplateCreationRequest,
  RequestMedicationTemplateUpdateRequest,
  RequestMembershipPlanUpdateRequest,
  RequestPricePointCreationRequest,
  RequestProgramsUpdateRequest,
  UpdateMedicationClassPriorityRequest,
  UpdatePartnerCouponConfigStatusRequest,
  UpdateRefillSettingsRequest
} from './clinicalTools.types';

// A price point holds one row per partner coupon, so a page comfortably covers every realistic
// configuration while still keeping the request bounded.
export const PARTNER_COUPON_CONFIG_PAGE_SIZE = 25;

type EntityTag = 'MedicationTemplates' | 'Program' | 'MembershipPlan';

const entityTagByAuditType: Partial<Record<ClinicalToolsAuditHistoryType, EntityTag>> = {
  'medication-templates': 'MedicationTemplates',
  'membership-plan': 'MembershipPlan',
  programs: 'Program'
};

const toIdString = (id?: MongoObjectId | string): string => {
  if (id == null) return '';
  return (typeof id === 'string' ? id : String(id.$oid ?? '')).trim();
};

// Invalidates both the entity's LIST query and, for updates (targetId present), its own detail
// query — without the latter, approving an edit leaves the previously-fetched detail cached with
// its pre-approval values, so reopening the record (e.g. the medication template editor) shows
// stale data even though the approval succeeded.
const invalidationForAuditResult = (result?: AuditHistoryEntryResponse) => {
  const auditType = result?.data?.type;
  const tag = auditType ? entityTagByAuditType[auditType] : undefined;
  if (!tag) return [];
  const targetId = toIdString(result?.data?.targetId);
  return targetId
    ? [
        { id: 'LIST', type: tag },
        { id: targetId, type: tag }
      ]
    : [{ id: 'LIST', type: tag }];
};

export const clinicalToolsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    approveAuditHistory: build.mutation<AuditHistoryEntryResponse, { auditId: string }>({
      invalidatesTags: (result, _error, { auditId }) => [
        { id: 'LIST', type: 'ClinicalToolsAuditHistory' },
        { id: auditId, type: 'ClinicalToolsAuditHistory' },
        ...invalidationForAuditResult(result)
      ],
      query: ({ auditId }) => ({
        method: 'POST',
        url: `/clinical-tools/audit-history/${auditId}/approve`
      })
    }),
    createPartnerCouponConfig: build.mutation<
      PartnerCouponConfigResponse,
      CreatePartnerCouponConfigRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'PartnerCouponConfig' }],
      query: (body) => ({
        body,
        method: 'POST',
        url: '/clinical-tools/partner-coupon-configs'
      }),
      transformResponse: (response: { data: PartnerCouponConfigResponse }) => response.data
    }),
    getAuditHistoryEntry: build.query<AuditHistoryEntryResponse, { auditId: string }>({
      providesTags: (_result, _error, { auditId }) => [
        { id: auditId, type: 'ClinicalToolsAuditHistory' }
      ],
      query: ({ auditId }) => ({
        method: 'GET',
        url: `/clinical-tools/audit-history/${auditId}`
      })
    }),
    getMaxioComponentPricePoints: build.query<
      MaxioComponentPricePointOption[],
      { componentId: string }
    >({
      query: ({ componentId }) => ({
        method: 'GET',
        url: `/clinical-tools/membership-plans/billing/components/${componentId}/price-points`
      }),
      transformResponse: (response: { data: { pricePoints: MaxioComponentPricePointOption[] } }) =>
        response.data.pricePoints
    }),
    getMaxioComponents: build.query<MaxioComponentOption[], void>({
      query: () => ({
        method: 'GET',
        url: '/clinical-tools/membership-plans/billing/components'
      }),
      transformResponse: (response: { data: { components: MaxioComponentOption[] } }) =>
        response.data.components
    }),
    getMaxioProductPricePoints: build.query<
      MaxioProductPricePointOption[],
      { productId?: string } | void
    >({
      query: (args) => ({
        method: 'GET',
        params: args && args.productId ? { productId: args.productId } : undefined,
        url: '/clinical-tools/membership-plans/billing/product-price-points'
      }),
      transformResponse: (response: { data: { pricePoints: MaxioProductPricePointOption[] } }) =>
        response.data.pricePoints
    }),
    // Singleton config — the response always carries every DrugClass wire value as a key (`null`
    // when never ranked), so the UI never needs its own list of classes.
    getMedicationClassPriority: build.query<MedicationClassPriorityResponse, void>({
      providesTags: [{ id: 'SINGLETON', type: 'MedicationClassPriority' }],
      query: () => ({
        method: 'GET',
        url: '/admin/medication-class-priority'
      }),
      transformResponse: (response: { data: MedicationClassPriorityResponse }) => response.data
    }),
    getMembershipPlansFullPayload: build.query<FullMembershipPlanProps[], void>({
      providesTags: [{ id: 'LIST', type: 'MembershipPlan' }],
      query: () => ({
        params: {
          limit: 100,
          pageNo: 1
        },
        url: '/clinical-tools/membership-plans/full-payload'
      }),
      transformResponse: (response: { data: FullMembershipPlanProps[] }) => response.data
    }),
    // `coupons` is read from Maxio and narrowed to the plan; Maxio cannot scope by price point, so
    // the price point is sent for symmetry rather than to filter. The args are the cache key, so each
    // price point gets its own entry rather than reusing whichever one was opened first.
    getPartnerCouponConfigMeta: build.query<
      PartnerCouponConfigMetaResponse,
      PartnerCouponConfigMetaRequest
    >({
      query: ({ planCode, pricePointId }) => ({
        method: 'GET',
        params: { planCode, pricePointId },
        url: '/clinical-tools/partner-coupon-configs/meta'
      }),
      transformResponse: (response: { data: PartnerCouponConfigMetaResponse }) => response.data
    }),
    getPricePointCategories: build.query<string[], void>({
      query: () => ({
        method: 'GET',
        url: '/clinical-tools/price-point-categories'
      }),
      transformResponse: (response: { data: string[] }) => response.data
    }),
    getProgramsFullPayload: build.query<ProgramsFullPayloadResponse, void>({
      providesTags: [{ id: 'LIST', type: 'Program' }],
      query: () => ({
        method: 'GET',
        url: '/clinical-tools/get-full-programs-payload'
      }),
      transformResponse: (response: ProgramsFullPayloadResponse) => response
    }),
    getRefillSettings: build.query<RefillSettingsResponse, void>({
      providesTags: [{ id: 'SINGLETON', type: 'RefillSettings' }],
      query: () => ({
        method: 'GET',
        url: '/admin/refill-settings'
      }),
      transformResponse: (response: { data: RefillSettingsResponse }) => response.data
    }),
    // Unsummarised list, used where the caller needs `proposedChanges` values — currently only the
    // membership-plan form, which reads the proposed price points out of pending requests. Always
    // call it with a `status`/`type` filter: unfiltered it returns every entry ever filed.
    listAuditHistory: build.query<ListAuditHistoryResponse, ListAuditHistoryRequest | void>({
      providesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: (params) => ({
        method: 'GET',
        params: params ?? undefined,
        url: '/clinical-tools/audit-history'
      })
    }),
    // One page of the requests table. `summary` drops the `proposedChanges` payloads the table
    // never renders, and `totalCount`/`statusCounts` ride in the envelope so the pagination control
    // and the status tabs no longer need the whole collection client-side.
    listAuditHistoryPage: build.query<ListAuditHistorySummaryResponse, ListAuditHistoryRequest>({
      providesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: (params) => ({
        method: 'GET',
        params: { ...params, summary: true },
        url: '/clinical-tools/audit-history'
      }),
      transformResponse: (response: ListAuditHistorySummaryEnvelope) => ({
        data: response.data ?? [],
        statusCounts: response.info?.statusCounts ?? {
          approved: 0,
          'needs-approval': 0,
          rejected: 0
        },
        totalCount: response.info?.totalCount ?? response.data?.length ?? 0
      })
    }),
    listMedicationClassPriorityHistory: build.query<
      MedicationClassPriorityHistoryResponse,
      ListMedicationClassPriorityHistoryRequest | void
    >({
      providesTags: [{ id: 'LIST', type: 'MedicationClassPriorityHistory' }],
      query: (params) => ({
        method: 'GET',
        params: params ?? undefined,
        url: '/admin/medication-class-priority/history'
      }),
      transformResponse: (response: { data: MedicationClassPriorityHistoryResponse }) =>
        response.data
    }),
    // Every status is returned when `status` is omitted, archived included, so a config the removal
    // cron is still working through stays visible to the operator who archived it.
    listPartnerCouponConfigs: build.query<
      PartnerCouponConfigListResponse,
      ListPartnerCouponConfigsRequest
    >({
      providesTags: [{ id: 'LIST', type: 'PartnerCouponConfig' }],
      query: ({ pageNo, planCode, pricePointId, status }) => ({
        method: 'GET',
        params: {
          limit: PARTNER_COUPON_CONFIG_PAGE_SIZE,
          pageNo: pageNo ?? 1,
          planCode,
          pricePointId,
          ...(status ? { status } : {})
        },
        url: '/clinical-tools/partner-coupon-configs'
      }),
      // `totalCount` rides in the envelope's `info`, not its `data`, so it is folded in here rather
      // than leaking the envelope's shape into every consumer.
      transformResponse: (response: {
        data: { configs: PartnerCouponConfigListResponse['configs'] };
        info?: { totalCount?: number };
      }) => ({
        configs: response.data.configs,
        totalCount: response.info?.totalCount ?? response.data.configs.length
      })
    }),
    listRefillSettingsHistory: build.query<
      RefillSettingsHistoryResponse,
      ListRefillSettingsHistoryRequest | void
    >({
      providesTags: [{ id: 'LIST', type: 'RefillSettingsHistory' }],
      query: (params) => ({
        method: 'GET',
        params: params ?? undefined,
        url: '/admin/refill-settings/history'
      }),
      transformResponse: (response: { data: RefillSettingsHistoryResponse }) => response.data
    }),
    rejectAuditHistory: build.mutation<AuditHistoryEntryResponse, { auditId: string }>({
      invalidatesTags: (result, _error, { auditId }) => [
        { id: 'LIST', type: 'ClinicalToolsAuditHistory' },
        { id: auditId, type: 'ClinicalToolsAuditHistory' },
        ...invalidationForAuditResult(result)
      ],
      query: ({ auditId }) => ({
        method: 'POST',
        url: `/clinical-tools/audit-history/${auditId}/reject`
      })
    }),
    requestMedicationTemplateCreation: build.mutation<
      AuditHistoryEntryResponse,
      RequestMedicationTemplateCreationRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: ({ body, reason }) => ({
        body: { ...body, ...(reason ? { reason } : {}) },
        method: 'POST',
        url: '/clinical-tools/medication-templates/request-medication-template-create'
      })
    }),
    requestMedicationTemplateUpdate: build.mutation<
      AuditHistoryEntryResponse,
      RequestMedicationTemplateUpdateRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: ({ body, medicationId, reason }) => ({
        body: { ...body, ...(reason ? { reason } : {}) },
        method: 'POST',
        url: `/clinical-tools/medication-templates/${medicationId}/request-medication-template-update`
      })
    }),
    requestMembershipPlanUpdate: build.mutation<
      AuditHistoryEntryResponse,
      RequestMembershipPlanUpdateRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: ({ body, planId, reason }) => ({
        body: { ...body, ...(reason ? { reason } : {}) },
        method: 'POST',
        url: `/clinical-tools/membership-plans/${planId}/request-membership-plan-update`
      })
    }),
    requestPricePointCreation: build.mutation<
      AuditHistoryEntryResponse,
      RequestPricePointCreationRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: ({ body, planId, reason }) => ({
        body: { ...body, ...(reason ? { reason } : {}) },
        method: 'POST',
        url: `/clinical-tools/membership-plans/${planId}/request-price-point`
      })
    }),
    requestProgramsUpdate: build.mutation<AuditHistoryEntryResponse, RequestProgramsUpdateRequest>({
      invalidatesTags: [{ id: 'LIST', type: 'ClinicalToolsAuditHistory' }],
      query: ({ body, programId, reason }) => ({
        body: { ...body, ...(reason ? { reason } : {}) },
        method: 'POST',
        url: `/clinical-tools/programs/${programId}/request-programs-update`
      })
    }),
    updateMedicationClassPriority: build.mutation<
      MedicationClassPriorityResponse,
      UpdateMedicationClassPriorityRequest
    >({
      invalidatesTags: [
        { id: 'SINGLETON', type: 'MedicationClassPriority' },
        { id: 'LIST', type: 'MedicationClassPriorityHistory' }
      ],
      query: (body) => ({
        body,
        method: 'PUT',
        url: '/admin/medication-class-priority'
      }),
      transformResponse: (response: { data: MedicationClassPriorityResponse }) => response.data
    }),
    // Status is the only mutation a config accepts: coupon and scoping are immutable, so changing a
    // coupon means archiving one config and creating another.
    updatePartnerCouponConfigStatus: build.mutation<
      PartnerCouponConfigResponse,
      UpdatePartnerCouponConfigStatusRequest
    >({
      invalidatesTags: [{ id: 'LIST', type: 'PartnerCouponConfig' }],
      query: ({ configId, status }) => ({
        body: { status },
        method: 'PATCH',
        url: `/clinical-tools/partner-coupon-configs/${configId}/status`
      }),
      transformResponse: (response: { data: PartnerCouponConfigResponse }) => response.data
    }),
    updateRefillSettings: build.mutation<RefillSettingsResponse, UpdateRefillSettingsRequest>({
      invalidatesTags: [
        { id: 'SINGLETON', type: 'RefillSettings' },
        { id: 'LIST', type: 'RefillSettingsHistory' }
      ],
      query: (body) => ({
        body,
        method: 'PUT',
        url: '/admin/refill-settings'
      }),
      transformResponse: (response: { data: RefillSettingsResponse }) => response.data
    })
  })
});

export const {
  useApproveAuditHistoryMutation,
  useCreatePartnerCouponConfigMutation,
  useGetAuditHistoryEntryQuery,
  useGetMaxioComponentPricePointsQuery,
  useGetMaxioComponentsQuery,
  useGetMaxioProductPricePointsQuery,
  useGetMedicationClassPriorityQuery,
  useGetMembershipPlansFullPayloadQuery,
  useGetPartnerCouponConfigMetaQuery,
  useGetPricePointCategoriesQuery,
  useGetProgramsFullPayloadQuery,
  useGetRefillSettingsQuery,
  useLazyGetAuditHistoryEntryQuery,
  useLazyListAuditHistoryQuery,
  useLazyListMedicationClassPriorityHistoryQuery,
  useLazyListRefillSettingsHistoryQuery,
  useListAuditHistoryPageQuery,
  useListAuditHistoryQuery,
  useListMedicationClassPriorityHistoryQuery,
  useListPartnerCouponConfigsQuery,
  useListRefillSettingsHistoryQuery,
  useRejectAuditHistoryMutation,
  useRequestMedicationTemplateCreationMutation,
  useRequestMedicationTemplateUpdateMutation,
  useRequestMembershipPlanUpdateMutation,
  useRequestPricePointCreationMutation,
  useRequestProgramsUpdateMutation,
  useUpdateMedicationClassPriorityMutation,
  useUpdatePartnerCouponConfigStatusMutation,
  useUpdateRefillSettingsMutation
} = clinicalToolsApi;
